import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertOctagon, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RESTRICAO_EMPRESAS, RESTRICAO_TIPOS, empresaLabel } from '../lib/restricoes';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const LeadRestricoesPanel = ({ lead, onSaved }) => {
  const [items, setItems] = useState(lead.restricoes || []);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setItems(lead.restricoes || []); }, [lead.restricoes]);

  const upd = (i, k, v) => setItems((arr) => arr.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/leads/${lead.id}/restricoes`, { restricoes: items.filter((r) => r.empresa) });
      toast.success('Dívidas / restrições guardadas');
      setEditing(false);
      onSaved?.(data.restricoes);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao guardar'); } finally { setSaving(false); }
  };

  return (
    <Card className={`rounded-2xl ${items.length ? 'border-rose-200' : 'border-slate-100'}`} data-testid="lead-restricoes-panel">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold flex items-center gap-2 whitespace-nowrap"><AlertOctagon className="w-4 h-4 text-rose-600" />Dívidas / Restrições</CardTitle>
        {!editing && <Button size="sm" variant="outline" onClick={() => { setEditing(true); if (!items.length) setItems([{ empresa: '', tipo: 'divida', nota: '' }]); }} data-testid="restricoes-edit-btn">Editar</Button>}
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        {!editing && !items.length && <p className="text-xs text-slate-500" data-testid="restricoes-empty">Sem dívidas ou restrições registadas.</p>}
        {!editing && items.map((r, i) => (
          <div key={i} className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2" data-testid={`restricao-${i}`}>
            <p className="text-sm font-semibold text-rose-700">{RESTRICAO_TIPOS[r.tipo] || 'Dívida'}: {empresaLabel(r.empresa)}</p>
            {r.nota && <p className="text-xs text-rose-900/80 mt-0.5">{r.nota}</p>}
          </div>
        ))}
        {editing && (
          <>
            {items.map((r, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-1.5" data-testid={`restricao-edit-${i}`}>
                <div className="flex gap-1.5">
                  <select value={r.empresa} onChange={(e) => upd(i, 'empresa', e.target.value)} className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid={`restricao-empresa-${i}`}>
                    <option value="">Empresa…</option>
                    {RESTRICAO_EMPRESAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select value={r.tipo} onChange={(e) => upd(i, 'tipo', e.target.value)} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid={`restricao-tipo-${i}`}>
                    {Object.entries(RESTRICAO_TIPOS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <button type="button" onClick={() => setItems((a) => a.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600 px-1" data-testid={`restricao-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <input value={r.nota || ''} onChange={(e) => upd(i, 'nota', e.target.value)} maxLength={300} placeholder="Nota (ex.: valor em dívida, desde quando)" className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid={`restricao-nota-${i}`} />
              </div>
            ))}
            <button type="button" onClick={() => setItems((a) => [...a, { empresa: '', tipo: 'divida', nota: '' }])} className="text-xs text-[#2D3E6F] flex items-center gap-1 hover:underline" data-testid="restricao-add"><Plus className="w-3.5 h-3.5" />Adicionar empresa</button>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => { setItems(lead.restricoes || []); setEditing(false); }}>Cancelar</Button>
              <Button size="sm" disabled={saving} onClick={save} className="bg-rose-600 hover:bg-rose-700" data-testid="restricoes-save-btn">Guardar</Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
